import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box } from '@mui/material';
import { FC, KeyboardEvent, MouseEvent, ReactNode, memo, useCallback } from 'react';
import ReactGA from 'react-ga4';
import { resolveRowVehicleOverride, ShiftTruckRowInfo } from 'src/components/dispatch/shiftTruckRowInfo';
import { DRAG_HANDLE_WIDTH, VEHICLE_CARD_HEIGHT } from 'src/components/dispatch/vehicleCardStyles';
import { ExpandedRowVM, ExpandedStopVM } from 'src/components/dispatch/vehicleRowExpand/buildExpandedRowVM';
import { ExpandedRowPanel } from 'src/components/dispatch/vehicleRowExpand/ExpandedRowPanel';
import { DispatchCard, Vehicle } from 'src/entities/dispatch';

import { RotationRow } from './adapter';
import { VehicleTariffTotals } from './buildGrossProfitByTruckId';
import { DisplayMetric } from './DisplayMetricToggle';
import { VehicleInfoView } from './VehicleInfoView';

export type VehicleListRowProps = {
  row: RotationRow;
  vehicle: Vehicle;
  /** グループ内の位置。角丸と「回転を追加できるのは最終行」の判定に使う */
  rowIndex: number;
  rowCount: number;
  /** 表示用の回転番号。rows の並びから親が計算した値 */
  displayRotationNumber: number;
  /** この行が属する勤務。編集メニューと削除の対象 */
  groupShiftTruckId: number | null;
  /** 回転の追加・削除の対象になる勤務 */
  rowDeliveryId: number;
  selected: boolean;
  noPlan: boolean;
  newlyAssigned: boolean;
  fixed: boolean;
  expanded: boolean;
  expandable: boolean;
  expandedVM: ExpandedRowVM | null;
  tariffTotals: VehicleTariffTotals | null;
  constraintAlerts?: readonly string[];
  driverName?: string;
  shiftTruckRowInfo?: ShiftTruckRowInfo;
  barColor?: string;
  displayMetrics: Set<DisplayMetric>;
  rotationEditable: boolean;
  /** 回転の並べ替え。undefined なら sortable にしない（ハンドルも出さない） */
  rotationSortableId?: string;
  rotationDraggable: boolean;
  reorderDisabledReason?: string;
  deleteShiftDisabledReason?: string;
  deleteRoundDisabledReason?: string;
  colorDisabledReason?: string;
  screenName: string;
  /** 先頭行に置く車両ハンドル。グループが作った要素をそのまま描く */
  vehicleDragHandle?: ReactNode;
  /** この勤務で新しく入った案件 ID。展開パネルの停車地ごとの判定に使う */
  newlyAssignedOrderIds?: ReadonlySet<number>;
  // 以下は id を引数に取る安定したハンドラ。行の中で束ねる
  onRowToggle: (rowId: string) => void;
  onToggleRowExpanded?: (rowId: string) => void;
  onAddRotation: (deliveryId: number) => void;
  onDeleteRotation: (deliveryId: number, rotationNumber: number) => void;
  onDeleteShift?: (shiftTruckId: number | null) => void;
  onTruckEdit?: (shiftTruckId: number | null, anchor: HTMLElement) => void;
  onDriverEdit?: (shiftTruckId: number | null, anchor: HTMLElement) => void;
  onPlateNumberEdit?: (shiftTruckId: number | null, anchor: HTMLElement) => void;
  onVehicleColorClick?: (truckId: number, anchor: HTMLElement) => void;
  onSelectStop?: (card: DispatchCard, vehicle: Vehicle, dispatchOrderIndex: number) => void;
  onReorderStops?: (row: RotationRow, stops: ExpandedStopVM[]) => void;
  registerRow?: (rowId: string, element: HTMLElement | null) => void;
};

/**
 * 車両リストの 1 行。memo 境界。
 *
 * Why not 親で行を組む: 行ごとのハンドラをインライン arrow で作り、ハンドル・展開トグルを
 * 親で組んで渡していたため、どこか 1 行の選択が変わるだけで全行の props 参照が変わり
 * VehicleInfoView の memo が効かなかった。id を引数に取る安定したハンドラを受け取って
 * 行の中で束ね、要素も行の中で組む。
 */
export const VehicleListRow: FC<VehicleListRowProps> = memo(({
  row,
  vehicle,
  rowIndex,
  rowCount,
  displayRotationNumber,
  groupShiftTruckId,
  rowDeliveryId,
  selected,
  noPlan,
  newlyAssigned,
  fixed,
  expanded,
  expandable,
  expandedVM,
  tariffTotals,
  constraintAlerts,
  driverName,
  shiftTruckRowInfo,
  barColor,
  displayMetrics,
  rotationEditable,
  rotationSortableId,
  rotationDraggable,
  reorderDisabledReason,
  deleteShiftDisabledReason,
  deleteRoundDisabledReason,
  colorDisabledReason,
  screenName,
  vehicleDragHandle,
  newlyAssignedOrderIds,
  onRowToggle,
  onToggleRowExpanded,
  onAddRotation,
  onDeleteRotation,
  onDeleteShift,
  onTruckEdit,
  onDriverEdit,
  onPlateNumberEdit,
  onVehicleColorClick,
  onSelectStop,
  onReorderStops,
  registerRow,
}) => {
  // 掴めない行も落とし先には要る（2 回転目を 1 回転目の上へ落として先頭にする操作）
  const rotationDragDisabled = !rotationSortableId || !rotationDraggable || !!reorderDisabledReason;
  // Why not 条件付きで呼ぶ: hooks は条件付きに呼べないので、並べ替えしない画面でも常に呼ぶ。
  //
  // Why not disabled に boolean を渡す: 並べ替えしない画面 (カード配車) の行は落とし先からも
  // 外さないといけないが、boolean では droppable を無効にできない。useSortable の
  // normalizeLocalDisabled (@dnd-kit/sortable 8.0.0 の sortable.esm.js:625-640) は boolean を
  // { draggable: bool, droppable: false } に展開し、droppable を false 固定にする
  // (boolean が両方に効くのは SortableContext の disabled prop 側の normalizeDisabled、同 58-67。別 API)。
  //
  // 落とし先から外さないと、この行は外側 (車両並べ替え用) の DndContext の有効な落とし先になり、
  // closestCenter が行を over に選びうる。すると ConvenienceDispatchVehicleList の
  // handleVehicleDragEnd が sortableKeys.indexOf(String(over.id)) で -1 を得て早期 return する
  // (sortableKeys は車両キー `shift-N` / `truck-N` だけなので行 id は必ず -1)。
  // ハンドルを掴んで落としたのに順番が変わらない、という無言の no-op になる。
  // droppable を無効にすると DroppableContainers.getEnabled() で衝突判定から外れる。
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: rotationSortableId ?? row.rowId,
    disabled: { draggable: rotationDragDisabled, droppable: !rotationSortableId },
  });

  const handleSelect = useCallback(() => {
    ReactGA.event('click', {
      screen_name: screenName,
      button_name: '車両行 選択',
      label: selected ? 'OFF' : 'ON',
    });
    onRowToggle(row.rowId);
  }, [screenName, selected, onRowToggle, row.rowId]);
  const handleSelectKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    // 内側の button (ハンドル・展開・車両名など) での Enter/Space は
    // そのボタン自身の click になる。ここで拾うと行選択まで二重に走る。
    // stopPropagation では止まらない (P3-a で実測) ので発生元で弾く。
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSelect();
    }
  }, [handleSelect]);
  const handleToggleExpanded = useCallback((e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    ReactGA.event('click', {
      screen_name: screenName,
      button_name: '車両行 展開',
      label: expanded ? 'OFF' : 'ON',
    });
    onToggleRowExpanded?.(row.rowId);
  }, [screenName, expanded, onToggleRowExpanded, row.rowId]);
  const handleAddRound = useCallback(() => { onAddRotation(rowDeliveryId); }, [onAddRotation, rowDeliveryId]);
  // Why not row.source.rotationNumber: 「回転を追加」で足した仮想行は source と row で
  // 回転番号がずれる。元コードと同じく row.rotationNumber を渡し、引数の出どころを変えない
  const handleDeleteRound = useCallback(
    () => { onDeleteRotation(rowDeliveryId, row.rotationNumber); },
    [onDeleteRotation, rowDeliveryId, row.rotationNumber],
  );
  const handleDeleteShift = useCallback(() => { onDeleteShift?.(groupShiftTruckId); }, [onDeleteShift, groupShiftTruckId]);
  const handleTruckClick = useCallback(
    (anchor: HTMLElement) => { onTruckEdit?.(groupShiftTruckId, anchor); },
    [onTruckEdit, groupShiftTruckId],
  );
  const handleDriverClick = useCallback(
    (anchor: HTMLElement) => { onDriverEdit?.(groupShiftTruckId, anchor); },
    [onDriverEdit, groupShiftTruckId],
  );
  const handlePlateNumberClick = useCallback(
    (anchor: HTMLElement) => { onPlateNumberEdit?.(groupShiftTruckId, anchor); },
    [onPlateNumberEdit, groupShiftTruckId],
  );
  const handleColorBarClick = useCallback(
    (anchor: HTMLElement) => { onVehicleColorClick?.(row.truckId, anchor); },
    [onVehicleColorClick, row.truckId],
  );
  const handleSelectCard = useCallback(
    (card: DispatchCard, dispatchOrderIndex: number) => { onSelectStop?.(card, vehicle, dispatchOrderIndex); },
    [onSelectStop, vehicle],
  );
  const handleReorderStops = useCallback(
    (stops: ExpandedStopVM[]) => { onReorderStops?.(row, stops); },
    [onReorderStops, row],
  );
  const setRowRef = useCallback(
    (element: HTMLElement | null) => { registerRow?.(row.rowId, element); },
    [registerRow, row.rowId],
  );

  const groupPosition = rowCount === 1
    ? 'single'
    : rowIndex === 0
      ? 'start'
      : rowIndex === rowCount - 1
        ? 'end'
        : 'middle';
  const rowRadius = groupPosition === 'single'
    ? '12px'
    : groupPosition === 'start'
      ? '12px 12px 0 0'
      : groupPosition === 'end'
        ? '0 0 12px 12px'
        : '0';
  // 既存の sx をそのまま括り出す。flex 系は足さない(false 枝の見た目を変えないため)
  const selectionSx = {
    height: VEHICLE_CARD_HEIGHT,
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'all 0.15s',
    borderRadius: rowRadius,
    // 新規割当の色はここに置かない。車両カードがこの Box と同じ大きさで
    // 重なるため、ここの背景は半透明のカード越しにしか出ず判別できない
    // (実測: カードの地はほぼ白)。ハイライトは VehicleInfoView 側で当てる
    bgcolor: selected ? 'hsl(var(--primary) / 0.05)' : 'hsl(var(--secondary) / 0.1)',
    ...(selected
      ? { boxShadow: 'inset 0 0 0 2px hsl(var(--primary))' }
      : { '&:hover': { bgcolor: 'hsl(var(--secondary) / 0.2)' } }),
  };
  // 展開トグルはカード内（色バーの右）に置く。カードの外に出すと
  // 行の背景と分離して「どの車両のものか」が曖昧になる。
  // Enter の二重発火は選択面側の handleSelectKeyDown で防ぐ。
  const expandToggle = expandable ? (
    <Box
      component="button"
      type="button"
      aria-label={expanded ? '折りたたむ' : '展開する'}
      aria-expanded={expanded}
      onClick={handleToggleExpanded}
      sx={{
        flexShrink: 0,
        width: 20,
        alignSelf: 'stretch',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: 'none',
        bgcolor: 'transparent',
        cursor: 'pointer',
        color: 'hsl(var(--muted-foreground))',
        p: 0,
      }}
    >
      {expanded
        ? <ExpandMoreIcon sx={{ fontSize: 18 }} />
        : <ChevronRightIcon sx={{ fontSize: 18 }} />}
    </Box>
  ) : undefined;
  // 掴めない行にはハンドルを描かない（車両ハンドルが同じ場所に居る）
  const rotationHandle = rotationSortableId && rotationDraggable ? (
    <Box
      component="button"
      type="button"
      aria-label="回転の並べ替え"
      disabled={rotationDragDisabled}
      // disabled 要素は MUI Tooltip の hover を拾えないので素の title で出す
      title={reorderDisabledReason}
      {...(rotationDragDisabled ? {} : attributes)}
      {...(rotationDragDisabled ? {} : listeners)}
      sx={{
        flexShrink: 0,
        width: DRAG_HANDLE_WIDTH,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: 'none',
        p: 0,
        bgcolor: 'transparent',
        color: 'hsl(var(--muted-foreground))',
        opacity: rotationDragDisabled ? 0.25 : 0.6,
        cursor: rotationDragDisabled ? 'default' : 'grab',
        '&:active': rotationDragDisabled ? undefined : { cursor: 'grabbing' },
        '&:hover': rotationDragDisabled ? undefined : { opacity: 1 },
      }}
    >
      <DragIndicatorIcon sx={{ fontSize: 14 }} />
    </Box>
  ) : undefined;

  // 並べ替えない画面では sortable のラッパを足さず、今までの DOM のまま返す。
  // Why not transform / transition を当てる: 掴めも落とせもしない行なので常に空になる
  const wrap = (content: ReactNode) => (rotationSortableId ? (
    <Box ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }}>
      {content}
    </Box>
  ) : content);

  const vehicleInfo = (
    <VehicleInfoView
      vehicle={vehicle}
      // 1 台に複数のドライバーが同じ時間帯で乗る勤務があるので、車両単位の
      // 代表 1 人ではなく行の乗務者を出す。引けないときは従来どおり
      // vehicle.driverName に落ちる（VehicleInfoView 側の ?? で処理）
      driverName={driverName}
      // 勤務時間・車番も shift_truck ごと。引けなければ車両単位の値に落ちる
      {...resolveRowVehicleOverride(shiftTruckRowInfo)}
      cards={row.cards}
      screenName={screenName}
      dragHandle={rowIndex === 0 ? vehicleDragHandle : rotationHandle}
      expandToggle={expandToggle}
      noPlan={noPlan}
      onDeleteShift={onDeleteShift && row.truckId != null ? handleDeleteShift : undefined}
      deleteShiftDisabledReason={deleteShiftDisabledReason}
      // バッジ表示は同じ勤務 (shift_truck) の中での行順 (1..N) で連番にする。
      // row.rotationNumber は (a) 「回転追加」で extraRotations に積まれた仮想行
      // が server 側 cycle と独立した値を保持する、(b) FR placeholder を
      // hiddenPlaceholderRotations で隠した時にギャップが残る、という二つの理由で
      // 連番にならないことがある。row.rotationNumber は rowId / 削除コールバック /
      // overrideRowCards / auto-dispatch の cycle として引き続き使うので変えず、
      // 表示専用の番号を渡す。
      //
      // truck 内の通し番号にしないのは、1 台に複数のドライバーが同じ時間帯で乗る
      // 勤務があるため。別の勤務が並んでいるだけなのに 2 回転目・3 回転目と出る
      // （/plans はこの状況を回転扱いしない）。
      rotationNumber={displayRotationNumber}
      // 車両合計なので 1 回転目の行にだけ渡す。全行に出すと
      // 同じ数字が並び「回転ごとの値」と誤読される。
      tariffTotals={tariffTotals}
      constraintAlerts={constraintAlerts}
      // Why not 選択中は出さない: 自動配車は車両を選択してから実行するため、
      // 対象車両は結果を見る時点でも選択されたまま。抑制するとハイライトが
      // ほとんど出ない。選択は枠 (行の Box の inset shadow) で示し分ける
      highlightNewlyAssigned={newlyAssigned}
      // delivery がある vehicle のみ表示されるため常に true
      enabled
      displayMetrics={displayMetrics}
      canAddRound={rowIndex === rowCount - 1}
      canDeleteRound={rowCount > 1}
      onAddRound={rotationEditable ? handleAddRound : undefined}
      onDeleteRound={rotationEditable ? handleDeleteRound : undefined}
      deleteRoundDisabledReason={deleteRoundDisabledReason}
      onTruckClick={onTruckEdit ? handleTruckClick : undefined}
      onDriverClick={onDriverEdit ? handleDriverClick : undefined}
      onPlateNumberClick={onPlateNumberEdit ? handlePlateNumberClick : undefined}
      rotationEditable={rotationEditable}
      fixed={fixed}
      barColor={barColor}
      onColorBarClick={onVehicleColorClick ? handleColorBarClick : undefined}
      colorDisabledReason={colorDisabledReason}
    />
  );

  if (!expandable) {
    // convenience はここを通る。いまのトップレベル要素をそのまま返す
    // (DOM の階層も sx も変えない)
    return wrap(
      <Box
        ref={registerRow ? setRowRef : undefined}
        role="button"
        tabIndex={0}
        onClick={handleSelect}
        onKeyDown={handleSelectKeyDown}
        sx={selectionSx}
        data-testid={row.rowId}
      >
        {vehicleInfo}
      </Box>,
    );
  }

  return wrap(
    <Box ref={registerRow ? setRowRef : undefined}>
      {/* ハンドル・展開トグルはカード (vehicleInfo) の中。
          Enter の二重発火は handleSelectKeyDown が currentTarget を
          見て防ぐ (内側の button の keydown はここまで上がってくる) */}
      <Box
        role="button"
        tabIndex={0}
        onClick={handleSelect}
        onKeyDown={handleSelectKeyDown}
        sx={{ ...selectionSx, display: 'flex', minWidth: 0 }}
        data-testid={row.rowId}
      >
        {vehicleInfo}
      </Box>
      {/* 72px の選択行の兄弟。内側に入れると overflow: hidden で切られる */}
      {expandedVM && (
        <ExpandedRowPanel
          vm={expandedVM}
          cards={row.cards}
          onSelectCard={onSelectStop ? handleSelectCard : undefined}
          onReorderStops={onReorderStops ? handleReorderStops : undefined}
          reorderDisabledReason={reorderDisabledReason}
          newlyAssignedOrderIds={newlyAssignedOrderIds}
        />
      )}
      {/* 計画が無い行は開いても見せるものが無い。サンプルもトグルの
          アイコンだけが変わり、パネルは出ない */}
    </Box>,
  );
});

VehicleListRow.displayName = 'VehicleListRow';
